/**
 * Iter XX.295 — Native IAP integrity gate (commercial P0).
 *
 * Wraps any native purchase CTA. A build that cannot configure
 * RevenueCat must NEVER present a tappable paid CTA — the tap would
 * dead-end after the player already decided to buy.
 *
 * Behaviour:
 *   · web                      → children unchanged (Stripe path).
 *   · native, ready/initializing → children unchanged. beginPlusPurchase
 *     stays the runtime guard for the transient initializing window
 *     (truthful "one moment" toast, never a fake purchase start).
 *   · native, unavailable       → truthful non-purchase state: the build
 *     cannot open subscriptions, so we say exactly that and point at
 *     the app update. No dead CTA, no "being turned on" fiction.
 */
import React from "react";
import { isNative } from "../lib/nativePlatform";
import { useNativePurchaseAvailability } from "../lib/useNativePurchaseAvailability";

export default function NativePurchaseCta({
  children,
  inline = false,
  testId,
}) {
  const availability = useNativePurchaseAvailability();

  if (!isNative() || availability.state !== "unavailable") {
    return <>{children}</>;
  }

  if (inline) {
    return (
      <span
        className="text-neutral-400"
        data-testid={testId || "native-purchase-unavailable-inline"}
      >
        Gully Plus{" "}
        <span className="text-[0.9em]">
          (update the app to subscribe)
        </span>
      </span>
    );
  }

  return (
    <div
      className="mt-2 rounded-xl border border-neutral-700/60 bg-neutral-900/60 px-3 py-2.5"
      data-testid={testId || "native-purchase-unavailable"}
    >
      <div className="text-[13px] font-medium text-neutral-200">
        Plus subscriptions need the latest Gully app
      </div>
      <div className="mt-0.5 text-[12px] text-neutral-400 leading-snug">
        This version can&apos;t open subscriptions. Update Gully from the
        App Store or Google Play, then subscribe to Plus.
      </div>
    </div>
  );
}
